import { Routes as FleetTireRoutes } from "ui/modules/fleet/tires/navigation";
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { useParams } from "react-router-dom";
import { FLEET_TIRE_SET_DETAIL_PAGE_TITLE, FLEET_TIRE_SET_EDIT_PAGE_TITLE } from "ui/components/PageTitle/consts";
import { TireSetDetailPageCmp, TireSetDetailPageProps } from "./types";
import { useCallback, useEffect, useState } from "react";
import { useTranslate } from "helpers";
import { BreadcrumbItem } from "ui/components/Breadcrumb/types";
import { SIDEBAR_ITEM_FLEET_TIRE_SET_SEARCH } from "app/components/Sidebar/consts";
import TabMenu from "./components/TabMenu/TabMenu";
import TireSetDetailCard from "./components/TireSetDetailCard/TireSetDetailCard";
import { deleteFleetTireSetAPI, getFleetTireSetAPI } from "services/api/fleet/tires/api";
import { FleetTireSet } from "services/api/fleet/tires/types";
import Badge from "ui/components/Badge/Badge";
import { getDimensionSet, getTireIcon, getTireType } from "services/api/fleet/tires/helpers";
import { Button } from "ui/components/Button/Button";
import TireSetEditCard from "../TireSetEditCard/TireSetEditCard";
import TireSetWriteOffModal from "../TireSetWriteOffModal/TireSetWriteOffModal";
import { Dialog } from "ui/components/Dialog";
import { Loader } from "ui/components/Loader";

const TireSetDetailPage: TireSetDetailPageCmp = (
  props: TireSetDetailPageProps
) => {
  const { id } = useParams();
  const { t } = useTranslate();

  const [tireSet, setTireSet] = useState<FleetTireSet | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showWriteOffModal, setShowWriteOffModal] = useState(false);
  const [showAddWidgetModal, setShowAddWidgetModal] = useState(false);

  useEffect(() => {
    if (id) fetchTireSet(id);
  }, [id]);

  const fetchTireSet = async (id: string) => {
    setIsLoading(true);
    try {
      const result = await getFleetTireSetAPI(id);
      setTireSet(result);
    } catch { /* */ }
    setIsLoading(false);
  };

  const handleEditSuccess = useCallback(() => {
    setShowEditModal(false);
    if (id) fetchTireSet(id);
  }, [id]);

  const handleWriteOffSuccess = useCallback(() => {
    setShowWriteOffModal(false);
    if (id) fetchTireSet(id);
  }, [id]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!id) return;
    try {
      await deleteFleetTireSetAPI(id, t);
      window.history.back();
    } catch { /* */ }
    setShowDeleteDialog(false);
  }, [id, t]);

  const breadcrumbs: BreadcrumbItem[] = [
    {
      text: SIDEBAR_ITEM_FLEET_TIRE_SET_SEARCH.text,
      route: SIDEBAR_ITEM_FLEET_TIRE_SET_SEARCH.route,
    },
  ];

  if (!id) return <></>;

  const tireType = tireSet?.tires ? getTireType(tireSet.tires) : null;
  const dimension = tireSet?.tires ? getDimensionSet(tireSet.tires) : "";

  return (
    <>
      <div>
        <div className="bg-white rounded-t-md">
          <PageHeader>
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-4">
                {tireSet?.tires && (
                  <div className="flex items-center justify-center w-[48px]">
                    {getTireIcon(tireSet.tires)}
                  </div>
                )}
                <div>
                  <div className="flex items-center gap-3">
                    <PageTitle
                      title={tireSet?.code
                        ? `Set guma [${tireSet.code}${dimension ? ` · ${dimension}` : ""}${tireType?.name ? ` · ${tireType.name}` : ""}]`
                        : t(FLEET_TIRE_SET_DETAIL_PAGE_TITLE.text)}
                      icon={FLEET_TIRE_SET_DETAIL_PAGE_TITLE.icon}
                      iconColor={FLEET_TIRE_SET_DETAIL_PAGE_TITLE.iconColor}
                    />
                    {tireSet && (
                      <Badge
                        text={tireSet.isActive ? "Aktivan" : "Neaktivan"}
                        variant={tireSet.isActive ? "success" : "warning"}
                        showDot
                      />
                    )}
                    {tireSet?.isWrittenOff && (
                      <Badge
                        text="Otpisano"
                        variant="danger"
                        showDot
                      />
                    )}
                  </div>
                  <Breadcrumb items={breadcrumbs} />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="primary"
                  startIcon="/media/icons/plus.svg"
                  onClick={() => setShowEditModal(true)}
                  disabled={!!tireSet?.isWrittenOff}
                >
                  Promjeni
                </Button>
                <Button
                  variant="remove"
                  startIcon="/media/icons/trash.svg"
                  onClick={() => setShowWriteOffModal(true)}
                  disabled={!!tireSet?.vehicle || !!tireSet?.isWrittenOff}
                >
                  Otpis
                </Button>
                {/* <div className="relative group">
                  <Button
                    variant="remove"
                    startIcon="/media/icons/trash.svg"
                    onClick={() => setShowDeleteDialog(true)}
                    disabled={!!tireSet?.vehicle || !!tireSet?.isWrittenOff}
                  >
                    Obriši
                  </Button>
                  {tireSet?.vehicle && (
                    <div className="absolute top-full right-0 mt-2 px-4 py-2.5 bg-slate-800 text-white text-sm rounded-sm min-w-[350px] text-wrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                      Set guma je montiran na vozilo i ne može se obrisati. Ukoliko ga želite obrisati napravite prvo transfer guma na skladište.
                    </div>
                  )}
                </div> */}
                <Button
                  variant="secondary"
                  startIcon="/media/icons/setting.svg"
                  onClick={() => setShowAddWidgetModal(true)}
                />{/* samo ikona */}
              </div>
            </div>
          </PageHeader>
          <TabMenu tireSetId={id} />
        </div>
        <div className="px-0 py-6 mx-[-1px] xl:px-6">
          {isLoading ? (
            <Loader isLoading={true}>{null}</Loader>
          ) : tireSet ? (
            <TireSetDetailCard
              tireSet={tireSet}
              showAddWidgetModal={showAddWidgetModal}
              setShowAddWidgetModal={setShowAddWidgetModal}
            />
          ) : null}
        </div>
      </div>

      {showEditModal && (
        <TireSetEditCard
          show={showEditModal}
          tireSetId={id}
          onSuccess={handleEditSuccess}
          onClose={() => setShowEditModal(false)}
          modalTitle={FLEET_TIRE_SET_EDIT_PAGE_TITLE.text}
        />
      )}

      {showWriteOffModal && (
        <TireSetWriteOffModal
          show={showWriteOffModal}
          tireSetId={id}
          onSuccess={handleWriteOffSuccess}
          onHide={() => setShowWriteOffModal(false)}
        />
      )}

      <Dialog
        open={showDeleteDialog}
        title="Brisanje seta guma"
        message="Jeste li sigurni da želite obrisati ovaj set guma?"
        confirmText="Obriši"
        cancelText="Odustani"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setShowDeleteDialog(false)}
      />
    </>
  );
};

TireSetDetailPage.route = FleetTireRoutes.FleetTireSetDetailPageRouter;

export default TireSetDetailPage;
